import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useState,
} from "react";
import PropTypes from "prop-types";
import { useGetObjectives, useLoadObjectiveBranch } from "./hooks";
import { GOALS_ACTIONS } from "./actionTypes";
import { goalsReducer, initialState } from "./reducers";

const GoalsExploreContext = createContext();

export const useGoalsExploreContext = () => {
  const context = useContext(GoalsExploreContext);
  if (!context) {
    throw new Error(
      "useGoalsExploreContext must be used within GoalsExploreProvider"
    );
  }
  return context;
};

const GoalsExploreProvider = ({
  initialObjectiveIds = [],
  childObjectivesDepth = 1,
  includeChildObjectives = false,
  skipInitialLoad = false,
  includeParentObjectives = false,
  children,
}) => {
  const [state, dispatch] = useReducer(goalsReducer, initialState);
  const [loadingNodeIds, setLoadingNodeIds] = useState({});

  const handleRootDataLoaded = useCallback((data) => {
    const objectives = data?.searchObjectives?.objectives || [];

    dispatch({
      type: GOALS_ACTIONS.LOAD_ROOT_OBJECTIVES,
      payload: { objectives },
    });
  }, []);

  const handleBranchDataLoaded = useCallback((data, parentNodeId) => {
    const objectives = data?.searchObjectives?.objectives || [];

    dispatch({
      type: GOALS_ACTIONS.LOAD_BRANCH_OBJECTIVES,
      payload: { objectives, parentNodeId },
    });
  }, []);

  const { loading: queryLoading, error: queryError } = useGetObjectives({
    onCompleted: handleRootDataLoaded,
    objectiveIds: initialObjectiveIds,
    maxChildDepth: childObjectivesDepth,
    includeChildObjectives,
    includeParentObjectives,
    skip: skipInitialLoad || state.treeData.length > 0,
  });

  const [loadBranchQuery, { loading: branchLoading }] =
    useLoadObjectiveBranch();

  const loadBranch = useCallback(
    ({
      nodeId,
      maxChildDepth = 1,
      excludeBaseObjectives = false,
      includingChildObjectives = true,
      includingParentObjectives = true,
    }) => {
      // Set loading state for this node
      setLoadingNodeIds((prev) => ({ ...prev, [nodeId]: true }));

      // Load the branch data
      loadBranchQuery({
        objectiveIds: [nodeId],
        maxChildDepth,
        excludeBaseObjectives,
        inputOverrides: {
          includeChildObjectives: includingChildObjectives,
          includeParentObjectives: includingParentObjectives,
        },
      })
        .then((result) => {
          if (result?.data) {
            handleBranchDataLoaded(result.data, nodeId);
          }
        })
        .finally(() => {
          setLoadingNodeIds((prev) => {
            const newState = { ...prev };
            delete newState[nodeId];
            return newState;
          });
        });
    },
    [loadBranchQuery, handleBranchDataLoaded]
  );

  const contextValue = {
    ...state,
    loading: state.loading || queryLoading || branchLoading,
    error: state.error || queryError,
    loadBranch,
    loadingNodeIds,
    dispatch,
  };

  return (
    <GoalsExploreContext.Provider value={contextValue}>
      {children}
    </GoalsExploreContext.Provider>
  );
};

GoalsExploreProvider.propTypes = {
  children: PropTypes.node.isRequired,
  initialObjectiveIds: PropTypes.arrayOf(PropTypes.string),
  skipInitialLoad: PropTypes.bool,
  childObjectivesDepth: PropTypes.number,
  includeChildObjectives: PropTypes.bool,
  includeParentObjectives: PropTypes.bool,
};

export default GoalsExploreProvider;
